import ApiRoutes from '@common/defs/api-routes';
import { ROLE } from '@modules/permissions/defs/types';
import { User } from '@modules/users/defs/types';
import useItems, { UseItemsOptions, defaultOptions } from '@common/hooks/useItems';
import { Upload } from '@modules/uploads/defs/types';
import useApi, { ApiResponse, FetchApiOptions } from '@common/hooks/useApi';
import useAuth from '@modules/auth/hooks/api/useAuth';
import { Id } from '@common/defs/types';
import useRegions from '@common/hooks/useRegions';
import useSWRImmutable from 'swr/immutable';

export interface CreateOneInput {
  email: string;
  password: string;
  role: ROLE;
  avatar: Blob | Upload | null;
  documents: Blob[] | Upload[] | null;
}

export interface UpdateOneInput {
  email: string;
  password?: string;
  role: ROLE;
  avatar: Blob | Upload | null;
  documents: Blob[] | Upload[] | null;
}

export interface GiveFeedbackInput {
  feedback: string;
}

export type UpsertOneInput = CreateOneInput | UpdateOneInput;

export interface WaitlistData {
  regionId: Id;
  regionName: string;
  usersInRegion: number;
  showUsersInRegion: boolean;
  waitlistMax: number;
  isInWaitlist: boolean;
  isOpenedRegion: boolean;
}
const useUsers = (opts: UseItemsOptions = defaultOptions) => {
  const { user } = useAuth();
  const apiRoutes = ApiRoutes.Users;
  const useItemsHook = useItems<User, CreateOneInput, UpdateOneInput>(apiRoutes, opts);
  const fetchApi = useApi();
  const { getRegionByName } = useRegions();

  const { data: waitlistData } = useSWRImmutable(
    user && user.isOnboardingCompleted ? apiRoutes.GetWaitlistData : null,
    async (url) => {
      const response = await fetchApi<WaitlistData>(url, {
        method: 'GET',
      });

      if (response.success && response.data) {
        const data = response.data;
        const region = getRegionByName(response.data.regionName);
        if (region) {
          const waitlistMax = region.waitlistMax;
          data.showUsersInRegion = data.usersInRegion >= waitlistMax * 0.5;
          return {
            ...data,
            waitlistMax,
            isInWaitlist: data.usersInRegion < waitlistMax,
            isOpenedRegion: ["Provence-Alpes-Côte d'Azur"].includes(data.regionName),
          };
        }
      }
      return null;
    }
  );

  const giveFeedback = async (
    input: GiveFeedbackInput,
    options?: FetchApiOptions
  ): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await fetchApi<{ success: boolean }>(apiRoutes.GiveFeedback, {
      method: 'POST',
      data: input,
      ...options,
    });
    return response;
  };

  return {
    ...useItemsHook,
    giveFeedback,
    waitlistData: waitlistData ?? null,
  };
};

export default useUsers;
